import moment from 'moment'

export const AUTH_USERS_PER_PAGE = 20

export const AUTH_EMAIL_TYPES = [
    {
        key: 'email_verification',
        text: 'Email verification',
        value: 'email_verification',
    },
    {
        key: 'password_set',
        text: 'Set password',
        value: 'password_set',
    },
]

export const TARGET_APP_OPTIONS = [
    { key: 'expert', text: 'Expert app', value: 'expert' },
    { key: 'client', text: 'Client app', value: 'client' },
    { key: 'admin', text: 'Admin app', value: 'admin' },
]

export const formatDate = (date, fallback = 'Not recorded') => {
    if (!date) return fallback
    return moment(date).format('MMM D, YYYY h:mm A')
}

export const formatRelativeDate = (date, fallback = 'Never') => {
    if (!date) return fallback
    return moment(date).fromNow()
}

export const getUserDisplayName = (user = {}) => {
    if (user.fullName) return user.fullName
    const name = [user.firstName, user.lastName].filter(Boolean).join(' ')
    return name || user.email || 'Unnamed user'
}

// Partner slugs come from the expert app's landing page URLs (e.g. /partners/vituity), so the
// slug is title-cased for display rather than keeping a second copy of the partner list here.
// The user's own value covers people who signed up through a partner but have not finished
// an expert profile yet.
export const getReferralPartner = (user = {}) => {
    const slug = user.expert?.referredByPartner || user.referredByPartner
    if (!slug) return null
    return slug.charAt(0).toUpperCase() + slug.slice(1)
}

export const getInitials = (user = {}) => {
    const name = getUserDisplayName(user)
    const parts = name.split(/\s+/).filter(Boolean)

    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    }

    return name.slice(0, 2).toUpperCase()
}

export const getTokenStatus = (token = {}) => {
    if (token.replaced) return 'Refreshed'
    if (token.revokedAt) return 'Revoked'
    if (token.usedAt) return 'Used'
    if (token.expiresAt && moment(token.expiresAt).isBefore(moment())) {
        return 'Expired'
    }
    return 'Active'
}

const getLinkEvent = (token, activeLabel, usedLabel, expiredLabel) => {
    const status = getTokenStatus(token)

    if (status === 'Used') {
        return {
            name: usedLabel,
            detail: `Completed ${formatRelativeDate(token.usedAt)}`,
        }
    }

    if (status === 'Expired') {
        return {
            name: expiredLabel,
            detail: `Expired ${formatRelativeDate(token.expiresAt)}`,
        }
    }

    if (status === 'Revoked') {
        return {
            name: `${activeLabel} revoked`,
            detail: `Revoked ${formatRelativeDate(token.revokedAt)}`,
        }
    }

    return {
        name: activeLabel,
        detail: `Created ${formatRelativeDate(token.createdAt)}`,
    }
}

export const getAuthEvent = (token = {}) => {
    const status = getTokenStatus(token)

    if (token.type === 'refresh') {
        if (token.replaced) {
            return {
                name: 'Session Refreshed',
                detail: `Access was renewed ${formatRelativeDate(token.revokedAt)}`,
            }
        }

        if (status === 'Revoked') {
            return {
                name: 'Success Logout',
                detail: `Logged out or revoked ${formatRelativeDate(token.revokedAt)}`,
            }
        }

        if (status === 'Expired') {
            return {
                name: 'Session Expired',
                detail: `Expired ${formatRelativeDate(token.expiresAt)}`,
            }
        }

        return {
            name: 'Success Login',
            detail: `Started ${formatRelativeDate(token.createdAt)}`,
        }
    }

    if (token.type === 'email-verification') {
        return getLinkEvent(
            token,
            'Verification email sent',
            'Email verified',
            'Verification link expired'
        )
    }

    if (token.type === 'password-reset') {
        return getLinkEvent(
            token,
            'Password reset email sent',
            'Password reset completed',
            'Password reset link expired'
        )
    }

    if (token.type === 'initial-password-setup') {
        return getLinkEvent(
            token,
            'Password setup email sent',
            'Password setup completed',
            'Password setup link expired'
        )
    }

    if (token.type === 'action') {
        return getLinkEvent(
            token,
            'Magic link issued',
            'Magic link completed',
            'Magic link expired'
        )
    }

    return {
        name: 'Auth event',
        detail: `Created ${formatRelativeDate(token.createdAt)}`,
    }
}
